'use client'

import { useEffect, useState } from 'react'
import type { StaticImageData } from 'next/image'
import { GOLD, NAVY, TEXT, TEXT_SECONDARY, BORDER, BORDER_GOLD, WHITE } from '@/app/lib/portal/theme'
import { Portrait } from './Portrait'

export interface DirectoryEntry {
  slug: string
  name: string
  credentials: string
  hospitals: string[]
  photo: StaticImageData | null
}

export interface DirectoryTab {
  key: string
  label: string
  /** Shown in place of the grid when no one is listed under the tab. */
  empty: string
}

const ALL = 'all'

/**
 * The provider directory under the leadership on /team: hospital tabs over a
 * grid of portrait cards. A card shows the photo (or a monogram until it
 * arrives), the name, and the credential — nothing else, as the headshot
 * request promised. The server renders the All tab, so every name is in the
 * HTML; the tabs filter in place, and a hash (/team#plano) opens one directly.
 * Everything arrives as props from the server page: this file imports no
 * roster or hospital module, so neither ships to the browser on its own.
 */
export function ProviderDirectory({
  sectionId,
  label,
  tabs,
  entries,
}: {
  sectionId: string
  label: string
  tabs: DirectoryTab[]
  entries: DirectoryEntry[]
}) {
  const [active, setActive] = useState(ALL)
  const tabKeys = tabs.map((t) => t.key).join(' ')

  useEffect(() => {
    const keys = tabKeys.split(' ')
    const fromHash = () => {
      const key = window.location.hash.slice(1)
      if (!keys.includes(key)) return
      setActive(key)
      document.getElementById(sectionId)?.scrollIntoView({ block: 'start' })
    }
    fromHash()
    window.addEventListener('hashchange', fromHash)
    return () => window.removeEventListener('hashchange', fromHash)
  }, [sectionId, tabKeys])

  const select = (key: string) => {
    setActive(key)
    const url = key === ALL ? `${window.location.pathname}${window.location.search}` : `#${key}`
    window.history.replaceState(null, '', url)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }
    const next = moves[e.key]
    if (next === undefined) return
    e.preventDefault()
    select(tabs[next].key)
    document.getElementById(`${sectionId}-tab-${tabs[next].key}`)?.focus()
  }

  const tab = tabs.find((t) => t.key === active) ?? tabs[0]
  const shown = tab.key === ALL ? entries : entries.filter((e) => e.hospitals.includes(tab.key))

  return (
    <div>
      <div role="tablist" aria-label={label} className="mb-7 flex flex-wrap gap-2">
        {tabs.map((t, i) => {
          const selected = t.key === tab.key
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`${sectionId}-tab-${t.key}`}
              aria-selected={selected}
              aria-controls={`${sectionId}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(t.key)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className="ms-tab rounded-full px-4 py-2 text-[13.5px] font-semibold"
              style={
                selected
                  ? { background: GOLD, color: NAVY, border: `1px solid ${GOLD}` }
                  : { background: 'transparent', color: TEXT_SECONDARY, border: `1px solid ${BORDER}` }
              }
            >
              {t.label}
            </button>
          )
        })}
      </div>

      <div role="tabpanel" id={`${sectionId}-panel`} aria-labelledby={`${sectionId}-tab-${tab.key}`}>
        {shown.length ? (
          <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5">
            {shown.map((p) => (
              <li key={p.slug} className="m-0">
                <figure className="m-0 h-full overflow-hidden" style={{ background: WHITE, border: `1px solid ${BORDER_GOLD}`, borderRadius: 14 }}>
                  <Portrait
                    photo={p.photo}
                    name={p.name}
                    credentials={p.credentials}
                    sizes="(min-width: 1024px) 190px, (min-width: 768px) 24vw, (min-width: 640px) 31vw, 46vw"
                    initialsSize={34}
                  />
                  <figcaption className="px-3.5 pb-4 pt-3">
                    <p className="m-0" style={{ fontFamily: 'Georgia, serif', fontSize: 16.5, lineHeight: 1.3, color: TEXT }}>
                      {p.name}
                    </p>
                    <p className="m-0 mt-1 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: GOLD }}>
                      {p.credentials}
                    </p>
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 rounded-2xl p-6 text-[15px] leading-[1.7]" style={{ color: TEXT_SECONDARY, background: WHITE, border: `1px solid ${BORDER}` }}>
            {tab.empty}
          </p>
        )}
      </div>
    </div>
  )
}
